import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeJobs } from '../../hooks/useRealtimeJobs';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { isMyItem, ownerLabel } from '../../utils/ownership';
import { timeAgo } from '../../utils/timeAgo';
import './JobsListPage.css';

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);

const IconFilter = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
);

const STATUS_LABEL: Record<'open' | 'filled' | 'closed', string> = {
  open: 'Open',
  filled: 'Filled',
  closed: 'Closed',
};

function isEligibleLinkedCandidate(status?: string): boolean {
  // Keep list-view counts aligned with Job Workspace linked-candidate eligibility.
  return status !== 'rejected_oversized';
}

function formatCreatedDate(createdAt: string): string {
  if (!createdAt) return '—';
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const JobsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { org } = useTenant();
  const { user } = useAuth();
  const { jobs, loading: jobsLoading } = useRealtimeJobs(org?.orgId);
  const { resumes } = useRealtimeResumes(org?.orgId);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'filled' | 'closed'>('all');
  const [onlyMine, setOnlyMine] = useState(false);

  const myUid = user?.uid;

  // Jobs in the current scope (after applying the "Only mine" toggle).
  // Status-filter counts and dropdown totals are computed from this set so they
  // stay accurate whether the user is looking at their own jobs or the org.
  const scopedJobs = useMemo(
    () => (onlyMine ? jobs.filter(j => isMyItem(j.uploadedBy, myUid)) : jobs),
    [jobs, onlyMine, myUid]
  );

  // Count resumes linked to each JD
  const resumeCountByJd = useMemo(() => {
    const counts: Record<string, number> = {};
    resumes
      .filter(r => isEligibleLinkedCandidate(r.status))
      .forEach(r => {
      (r.jdIds ?? []).forEach(jdId => {
        counts[jdId] = (counts[jdId] || 0) + 1;
      });
      });
    return counts;
  }, [resumes]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    let result = scopedJobs;

    if (statusFilter !== 'all') {
      result = result.filter(j => j.status === statusFilter);
    }

    if (query) {
      result = result.filter(j =>
        (j.title || '').toLowerCase().includes(query) ||
        (j.snippet || '').toLowerCase().includes(query)
      );
    }

    // Default newest-first by created date, regardless of status.
    return [...result].sort((a, b) => {
      const da = a.createdAt || '';
      const db = b.createdAt || '';
      if (da !== db) return db.localeCompare(da);
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [scopedJobs, search, statusFilter]);

  // Counts shown inside the status dropdown reflect the current scope.
  const scopedTotal = scopedJobs.length;
  const openCount = useMemo(() => scopedJobs.filter(j => j.status === 'open').length, [scopedJobs]);
  const filledCount = useMemo(() => scopedJobs.filter(j => j.status === 'filled').length, [scopedJobs]);
  const closedCount = useMemo(() => scopedJobs.filter(j => j.status === 'closed').length, [scopedJobs]);

  return (
    <div className="v2-page">
      <div className="v2-page-header">
        <h1>Jobs</h1>
        <p>Manage your job descriptions and find matching candidates</p>
      </div>

      {/* Command bar */}
      <div className="jobs-command-bar">
        <div className="v2-search-wrapper jobs-command-bar__search">
          <IconSearch />
          <input
            className="v2-search-input"
            placeholder="Search jobs by title or summary..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className="v2-btn v2-btn-primary jobs-command-bar__create" onClick={() => navigate('/jobs/new')}>
          <IconPlus /> New Job
        </button>
      </div>

      <div className="jobs-list-controls" style={{ marginBottom: 'var(--space-4)' }}>
        <label className="jobs-list-controls__only-mine">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={e => setOnlyMine(e.target.checked)}
            aria-label="Show only my jobs"
          />
          <span>Only mine</span>
        </label>
        <div className="jobs-list-controls__filter">
          <IconFilter />
          <select
            id="jobs-status-filter"
            className="jobs-list-controls__filter-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as 'all' | 'open' | 'filled' | 'closed')}
            aria-label="Filter jobs by status"
          >
            <option value="all">Filter: All ({scopedTotal})</option>
            <option value="open">Filter: Open ({openCount})</option>
            <option value="filled">Filter: Filled ({filledCount})</option>
            <option value="closed">Filter: Closed ({closedCount})</option>
          </select>
        </div>
        <span className="jobs-list-controls__count">{filtered.length} shown</span>
      </div>

      <div className="v2-card">
        <div className="v2-card-body-flush">
          {jobsLoading ? (
            <div className="v2-empty"><span className="v2-spinner" /> Loading jobs...</div>
          ) : filtered.length === 0 ? (
            <div className="v2-empty">
              <div className="v2-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
              </div>
              <h3>{search ? 'No matching jobs' : 'No jobs yet'}</h3>
              <p>{search ? 'Try a different search term.' : 'Click "Add New Job" above to post your first job description.'}</p>
            </div>
          ) : (
            <div className="jobs-grid-list">
              <div className="jobs-grid-list__header" role="presentation">
                <span>Job</span>
                <span>Owner</span>
                <span>Status</span>
                <span>Linked Candidates</span>
                <span>Created</span>
              </div>
              {filtered.map(job => (
                <div
                  key={job.jdId}
                  className="jobs-grid-list__row"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/jobs/${job.jdId}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/jobs/${job.jdId}`);
                    }
                  }}
                >
                  <div className="jobs-grid-list__job-cell">
                    <p className="jobs-grid-list__title" title={job.title || 'Untitled'}>
                      {job.title || 'Untitled'}
                    </p>
                    <p className="jobs-grid-list__snippet" title={job.snippet || ''}>
                      {job.snippet || 'No summary available'}
                    </p>
                    {job.tags && job.tags.length > 0 && (
                      <div className="jobs-grid-list__tags">
                        {job.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="jobs-grid-list__tag-badge" title={tag}>
                            {tag}
                          </span>
                        ))}
                        {job.tags.length > 3 && (
                          <span className="jobs-grid-list__tag-more" title={`${job.tags.length - 3} more tags`}>
                            +{job.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    className={`jobs-grid-list__owner-cell${job.uploadedBy ? '' : ' jobs-grid-list__owner-cell--muted'}`}
                    title={ownerLabel(job.uploadedBy, org?.members, myUid)}
                  >
                    {ownerLabel(job.uploadedBy, org?.members, myUid)}
                  </div>

                  <div className="jobs-grid-list__status-cell">
                    <span className={`jobs-status-chip jobs-status-chip--${job.status}`}>
                      {STATUS_LABEL[job.status]}
                    </span>
                  </div>

                  <div className="jobs-grid-list__count-cell">
                    {resumeCountByJd[job.jdId] || 0}
                  </div>

                  <div className="jobs-grid-list__date-cell" title={job.createdAt ? timeAgo(job.createdAt).title : ''}>
                    <span>{formatCreatedDate(job.createdAt)}</span>
                    <small>{job.createdAt ? timeAgo(job.createdAt).label : '—'}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
